import { QueryStatus, UseQueryOptions } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';

import { useDoClearSession } from '../../api/clear-session.api';
import { DiscoveryResponse, useDoDiscovery, Workspace } from '../../api/discovery.api';
import { Step } from '../../App';

type LoadDiscoveryResult = {
	status: QueryStatus;
	email?: string;
	workspace?: Workspace;
	onGoBack: () => void;
};

export const useLoadDiscovery = (queryOptions?: UseQueryOptions<DiscoveryResponse>): LoadDiscoveryResult => {
	const navigateTo = useNavigate();

	const { mutate: clearSession } = useDoClearSession({
		onSettled: () => navigateTo(Step.ENTER_EMAIL, { replace: true }),
	});

	const { workspaceId } = useParams<{ workspaceId: string }>();

	const queryData = useDoDiscovery<DiscoveryResponse>(
		{},
		{
			cacheTime: 0,
			retry: false,
			...queryOptions,
		}
	);

	const onGoBack = () => {
		const workspaces = queryData.data?.workspaces || [];

		const strategiesForWorkspace = workspaces.filter(({ id }) => id === workspaceId);
		if (strategiesForWorkspace.length === 2) {
			return navigateTo(Step.SELECT_AUTHENTICATION_MULTI_STRATEGY_STEP);
		}

		if (workspaces.length > 1) {
			return navigateTo(Step.SELECT_WORKSPACE);
		}

		clearSession();
		return navigateTo(Step.ENTER_EMAIL);
	};

	return {
		status: queryData.status,
		email: queryData.data?.email,
		workspace: queryData.data?.workspaces.find(({ id }) => id === workspaceId),
		onGoBack,
	};
};
